import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { useCalculator } from "@/contexts/CalculatorContext"
import { motion, AnimatePresence } from "framer-motion"

export function PeriodTabs({ children }: { children: React.ReactNode }) {
  const { 
    currentPeriod, 
    setCurrentPeriod, 
    getAllPeriods,
  } = useCalculator()
  
  // Function to calculate period dates dynamically based on current date
  const getPeriodDates = () => {
    const now = new Date()
    const currentYear = now.getFullYear()
    const currentMonth = now.getMonth() + 1 // 1-12
    
    // Determine which half-year we're in (1-6 = first half, 7-12 = second half)
    const isSecondHalf = currentMonth >= 7
    
    const monthNames = [
      'leden', 'únor', 'březen', 'duben', 'květen', 'červen',
      'červenec', 'srpen', 'září', 'říjen', 'listopad', 'prosinec'
    ]
    
    const periods = []
    
    for (let i = 0; i < 6; i++) {
      // Calculate how many half-years ahead this period is
      const halfYearsAhead = i
      
      // Calculate the target half-year
      const targetHalfYear = isSecondHalf ? 2 : 1 // 1 = first half, 2 = second half
      const totalHalfYears = targetHalfYear + halfYearsAhead
      
      // Calculate year and which half (1 or 2)
      const yearOffset = Math.floor((totalHalfYears - 1) / 2)
      const year = currentYear + yearOffset
      const halfInYear = ((totalHalfYears - 1) % 2) + 1 // 1 or 2
      
      const startMonth = halfInYear === 1 ? 1 : 7
      const endMonth = halfInYear === 1 ? 6 : 12
      
      const label = i === 0 ? "Aktuální půlrok" : `${i + 1}. půlrok`
      const date = `${monthNames[startMonth - 1]} ${year} - ${monthNames[endMonth - 1]} ${year}`
      
      periods.push({ id: i, label, date })
    }
    
    return periods
  }
  
  const periods = getPeriodDates()

  const periodData = getAllPeriods()


  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 20 : -20,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 20 : -20,
      opacity: 0
    })
  }

  return (
    <div className="space-y-0">

      <Tabs 
        value={currentPeriod.toString()} 
        onValueChange={(v) => setCurrentPeriod(parseInt(v))}
        className="w-full"
      >
        <TabsList className="grid w-full grid-cols-6">
          {periods.map((period) => {
            const data = periodData[period.id]
            const isActive = data.nastupy > 0 || data.contracts > 0
            
            return (
              <TabsTrigger
                key={period.id}
                value={period.id.toString()}
                className={`text-sm h-auto py-2 px-3 data-[state=active]:bg-white ${
                  isActive ? 'bg-green-50' : ''
                }`}
              >
                <div className="flex flex-col items-center">
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <span className="hidden md:inline">{period.label}</span>
                    <span className="lg:hidden">{period.id + 1}.</span>
                  </span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap hidden lg:block">
                    {period.date}
                  </span>
                </div>
              </TabsTrigger>
            )
          })}
        </TabsList>

        <AnimatePresence mode="sync" custom={currentPeriod}>
          <TabsContent 
            key={currentPeriod} 
            value={currentPeriod.toString()}
            className="mt-0 bg-white rounded-b-lg"
            forceMount
          >
            <motion.div
              key={`content-${currentPeriod}`}
              custom={currentPeriod}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { 
                  type: "spring", 
                  stiffness: 200,
                  damping: 25,
                  duration: 0.5
                },
                opacity: { 
                  duration: 0.3
                }
              }}
            >
              {children}
            </motion.div>
          </TabsContent>
        </AnimatePresence>
      </Tabs>
    </div>
  )
} 